import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import type { Auth } from 'firebase/auth';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthRequest, AuthResponse, AccountSummary } from './auth.models';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);

  private readonly apiUrl = `${environment.apiAuthUrl}/api/v1/auth`;
  private readonly commonHeaders = { 'x-requested-by': environment.requestedBy };

  private authInstance: Auth | null = null;

  private async getAuth(): Promise<Auth> {
    if (!this.authInstance) {
      const { initializeApp } = await import('firebase/app');
      const { getAuth } = await import('firebase/auth');
      const app = initializeApp(environment.firebase);
      this.authInstance = getAuth(app);
    }
    return this.authInstance;
  }

  async signInWithMicrosoft(): Promise<string> {
    const { OAuthProvider, signInWithPopup } = await import('firebase/auth');
    const auth = await this.getAuth();

    const provider = new OAuthProvider('microsoft.com');
    provider.setCustomParameters({
      tenant: '5daabe65-ff53-4336-a448-2f61ecf0b059',
      prompt: 'select_account',
    });
    provider.addScope('mail.read');

    const result = await signInWithPopup(auth, provider);
    return result.user.getIdToken();
  }

  async login(firebaseToken: string): Promise<AuthResponse> {
    const body: AuthRequest = { firebaseToken, productCode: environment.productCode };
    return firstValueFrom(
      this.http.post<AuthResponse>(`${this.apiUrl}/session/login`, body, {
        headers: this.commonHeaders,
        withCredentials: true,
      })
    );
  }

  async logout(): Promise<void> {
    try {
      await firstValueFrom(
        this.http.post(`${this.apiUrl}/logout`, {}, {
          headers: this.commonHeaders,
          withCredentials: true,
        })
      );
    } finally {
      const { signOut } = await import('firebase/auth');
      const auth = await this.getAuth();
      await signOut(auth);
    }
  }

  async getMe(): Promise<AccountSummary> {
    const response = await firstValueFrom(
      this.http.get<AuthResponse>(`${this.apiUrl}/me`, {
        headers: this.commonHeaders,
        withCredentials: true,
      })
    );
    return response.account;
  }
}
